import type { OversightEffectivenessByControl } from '@risksmart-app/trpc/src/types';
import type { GetLatestOversightEffectivenessByControlIdsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLatestOversightEffectivenessByControlIdsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

type UseGetLatestOversightEffectivenessByControlIdsArgs = {
  controlIds: string[];
};

/**
 * Reshapes the service-resolved "latest effectiveness per control" rows back
 * into the GraphQL query response shape so the page hook's shared client-side
 * resolution treats both branches uniformly. The service already reduced each
 * control to a single leaf, so one synthetic `control_test_oversight_result`
 * row per control is emitted; the shared resolver then trivially selects it.
 * `Id` / `TestDate` are unused by the resolver for a single-row-per-parent
 * input, so placeholders are safe.
 */
export const mapOversightEffectivenessByControlToGraphQL = (
  data: OversightEffectivenessByControl[]
): StrictMappedGraphQLSelection<GetLatestOversightEffectivenessByControlIdsQuery> => ({
  control_test_oversight_result: data.map((row) => ({
    Id: '',
    DesignEffectiveness: row.DesignEffectiveness,
    PerformanceEffectiveness: row.PerformanceEffectiveness,
    OverallEffectiveness: row.OverallEffectiveness,
    ParentControlId: row.ControlId,
    TestDate: '',
    CreatedAtTimestamp: row.CreatedAt ?? '',
  })),
});

export const useGetLatestOversightEffectivenessByControlIds = createQueryHook<
  UseGetLatestOversightEffectivenessByControlIdsArgs,
  OversightEffectivenessByControl[],
  GetLatestOversightEffectivenessByControlIdsQuery
>({
  trpcQueryOptions: (trpc, { controlIds }) =>
    trpc.frontend.oversight.effectivenessByControlIds.queryOptions({
      controlIds,
    }),
  mapTrpcDataToGraphQL: mapOversightEffectivenessByControlToGraphQL,
  graphqlDocument: GetLatestOversightEffectivenessByControlIdsDocument,
  graphqlVariables: ({ controlIds }) => ({ ControlIds: controlIds }),
  graphqlFetchPolicy: 'cache-and-network',
});
